import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { Swiper as SwiperProps } from 'swiper';
import { Mousewheel, Pagination } from 'swiper/modules';
import { usePageView } from '../support/tracking/usePageView.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Button } from '../components/button/Button.tsx';
import { Color } from '../components/common/index.ts';
import { IonText } from '@ionic/react';
import { CheckmarkIcon } from '../components/icons/Icons.tsx';
import { GeolocationService } from '../map/location/GeolocationService.ts';
import { PushNotificationService } from '../push-notifications/PushNotificationService.ts';
import { initCap } from '../support/helpers/format.ts';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';
import { useIsNative } from '../support/hooks/useIsNative.tsx';

import 'swiper/css';
import 'swiper/css/pagination';

interface IOnboardingSlide {
    heading: string;
    headingAtTop?: boolean;
    legend?: string;
    description?: string;
    legendColor?: Color;
    image: string;
    details: string[];
    action?: () => Promise<void>;
    actionStatus?: string;
    nativeOnly?: boolean;
}

const Heading: React.FC<{slide: IOnboardingSlide}> = ({ slide }) => (
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 400, marginBottom: 10}}>
        <IonText style={{minHeight: 30, fontSize: 14, fontWeight: 'bold'}} color={slide.legendColor || 'primary'}>{slide.legend}</IonText>
        <div style={{fontSize: 22, fontWeight: 'bold'}}>{slide.heading}</div>
        {slide.description &&
            <div style={{fontSize: 14, padding: '20px 40px 0', textAlign: 'center'}}>{slide.description}</div>
        }
    </div>
);

const Details: React.FC<{slide: IOnboardingSlide}> = ({ slide }) => (
    <div style={{display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 0'}}>
        {slide.details.map((detail, i) => (
            <div key={`detail-${i}`} style={{fontSize: 16, display: 'flex', alignItems: 'center', gap: 15, maxWidth: 320}}>
                <div style={{width: 20}}><CheckmarkIcon color="primary" size="medium" strokeWidth={65} /></div>
                <span>{detail}</span>
            </div>
        ))}
    </div>
);

const Buttons: React.FC<{slide: IOnboardingSlide, onCancel?: () => void, onDone: () => void, progress: number}> = ({ slide, onCancel, onDone, progress }) => {
    const swiper = useSwiper();
    const isNative = useIsNative();
    const onContinue = async ({ skipAction }: { skipAction?: boolean } = {}) => {
        if (!skipAction && slide.action) {
            await slide.action();
        }
        if (progress === 1) {
            onDone();
        } else {
            swiper.slideNext();
        }
    };
    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', padding: '0px 55px', textAlign: 'center'}}>
            {progress === 0 && onCancel &&
                <Button fill="clear" type="button" size="small" onClick={onCancel}>&lt; Back</Button>
            }
            {progress > 0 && !isNative &&
                <Button fill="clear" type="button" size="small" onClick={() => swiper.slidePrev()}>&lt; Back</Button>
            }
            {slide.action &&
                <Button fill="clear" type="button" size="small" onClick={() => onContinue({ skipAction: true })}>Skip for now</Button>
            }
            {!slide.action && slide.actionStatus &&
                <div style={{fontSize: 14, padding: '10px 0'}}>{slide.actionStatus}</div>
            }
            <Button expand="block" onClick={() => onContinue()}>{progress === 1 ? 'Done' : 'Continue'}</Button>
        </div>
    );
};

const PhoneContent: React.FC<{slide: IOnboardingSlide, onDone: () => void, onCancel?: () => void, progress: number}> = ({ slide, onDone, onCancel, progress }) => {
    return (
        <div style={{width: '100%', height: '100%', paddingTop: 'calc(var(--ion-safe-area-top) - 10px)', backgroundColor: 'var(--ion-color-light)'}}>
            {slide.headingAtTop &&
                <div style={{display: 'flex', justifyContent: 'center', paddingTop: 30, marginBottom: -90}}>
                    <Heading slide={slide} />
                </div>
            }
            <div style={{width: '100%', height: '100%', padding: '85px 0', marginTop: -115, textAlign: 'center'}}>
                <img style={{height: '100%', objectFit: 'cover'}} src={`./assets/images/onboarding/phone/${slide.image}`} alt="Screenshot" />
            </div>
            <div style={{position: 'absolute', borderTop: '1px solid var(--ion-color-step-100)', height: (slide.headingAtTop ? 305 : 355), bottom: 0, width: '100%', backgroundColor: 'white'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', width: '100%', padding: '20px 20px 35px'}}>
                    <div>
                        <div style={{minHeight: 10}}>
                            {!slide.headingAtTop &&
                                <Heading slide={slide} />
                            }
                        </div>
                        <Details slide={slide} />
                    </div>
                    <Buttons slide={slide} onCancel={onCancel} onDone={onDone} progress={progress} />
                </div>
            </div>
        </div>
    );
};

const TabletContent: React.FC<{slide: IOnboardingSlide, onDone: () => void, onCancel?: () => void, progress: number}> = ({ slide, onDone, onCancel, progress }) => {
    return (
        <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center'}}>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: 350, width: '40%'}}>
                <div>
                    <Heading slide={slide} />
                    <Details slide={slide} />
                </div>
                <Buttons slide={slide} onCancel={onCancel} onDone={onDone} progress={progress} />
            </div>
            <div style={{width: '60%', height: '100%', display: 'flex', alignItems: 'center', backgroundColor: 'var(--ion-color-light)'}}>
                <img style={{height: '100%', maxHeight: 800, objectFit: 'cover', objectPosition: 'left', padding: '30px 0 30px 50px'}} src={`./assets/images/onboarding/tablet/${slide.image}`} alt="Screenshot" />
            </div>
        </div>
    );
};

export const Onboarding: React.FC<{onDone: () => void, onCancel?: () => void}> = ({ onDone, onCancel }) => {
    usePageView('Onboarding');

    const tracker = useInstance(TrackingService);

    const isNative = useIsNative();
    const { isLargerScreen } = useBreakpoints();

    const locationService = useInstance(GeolocationService);
    const pushNotificationService = useInstance(PushNotificationService);

    const [locationPermission, setLocationPermission] = useState('disabled');
    const [notificationPermission, setNotificationPermission] = useState('disabled');

    useEffect(() => {
        tracker.track('Started Intercom Onboarding');
        if (isNative) {
            locationService.getPermissionStatus().then(setLocationPermission);
            pushNotificationService.getPermissionStatus().then(setNotificationPermission);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const locationAction = !['granted', 'denied', 'disabled'].includes(locationPermission) ? async () => {
        if (isNative) {
            setLocationPermission(await locationService.requestPermission());
        }
    } : undefined;

    const notificationsAction = !['granted', 'denied', 'disabled'].includes(notificationPermission) ? async () => {
        if (isNative) {
            setNotificationPermission(await pushNotificationService.requestPermission());
        }
    } : undefined;

    const statusMsg = (permission: string) => {
        return (['granted', 'denied'].includes(permission) ? `Permission ${initCap(permission)}` : '');
    };

    const slides: IOnboardingSlide[] = [
        {
            heading: 'Explore Airspace B4UFLY',
            legend: 'FAA-Approved Provider of B4UFLY',
            legendColor: 'success',
            image: '1-b4ufly.webp',
            details: ['Real-time FAA airspace data', 'Easily drop a pin and see all the rules', 'Get latest airspace info like TFRs']
        },
        {
            heading: 'Get LAANC Instantly',
            legend: 'FAA-Approved Provider of LAANC',
            image: '2-laanc.webp',
            details: ['Instantly see if the airspace supports LAANC', 'Request and get approved in seconds', 'Safely unlock more airspace']
        },
        {
            heading: 'Check Weather',
            image: '3-weather.webp',
            details: ['Get mission-specific weather', 'See hourly and 10-day forecasts', 'Upgrade to see real-time Windy Maps']
        },
        {
            heading: 'Enable Location',
            headingAtTop: true,
            action: locationAction,
            actionStatus: statusMsg(locationPermission),
            nativeOnly: true,
            description: 'We can’t find you accurate airspace and weather information if we can’t find you.',
            image: '4-location.webp',
            details: ['Show nearby airspace restrictions', 'Help quickly submit LAANC authorizations', 'Provide hyper-local weather forecasts']
        },
        {
            heading: 'Create Checklists',
            image: '5-checklists.webp',
            details: ['Create and reuse mission checklists', 'Log and save all your processes', 'Get push notifications to complete items']
        },
        {
            heading: 'Turn on Notifications',
            headingAtTop: true,
            action: notificationsAction,
            actionStatus: statusMsg(notificationPermission),
            nativeOnly: true,
            description: 'We can’t send you mission-critical alerts or reminders if we can’t reach you.',
            image: '6-notifications.webp',
            details: ['See mission checklist reminders', 'See mission-critical airspace updates', 'Stay connected with AutoPylot']
        },
        {
            heading: 'Track Equipment',
            image: '7-equipment.webp',
            details: ['Keep track of all your drone info', 'Auto-assign drones to missions', 'Create a log of drone activity, maintenance, incidents']
        },
        {
            heading: 'Manage Missions',
            image: '8-missions.webp',
            details: ['Quickly access all your mission details', 'Track and manage notes, checklists, weather, and airspace', 'Easily share your mission with others, or print a PDF']
        },
        {
            heading: 'Go Pro with AutoPylot+',
            image: '10-windy.webp',
            details: ['Skip the guesswork, not the mission', 'Your gut isn’t a weather tool, AutoPylot+ is', 'Turn flights into reports at the tap of a button']
        }
    ];

    const filteredSlides = slides
        .filter(s => !s.nativeOnly || isNative);

    const Content = isLargerScreen ? TabletContent : PhoneContent;

    return (
        <Swiper
            style={{height: '100%', width: '100%'}}
            modules={[Pagination, Mousewheel]}
            slidesPerView={1}
            centeredSlides
            mousewheel
            pagination={{ clickable: true }}

            // on swipe next: if slide has an action, go back to old slide, perform action, then go to new slide
            onBeforeTransitionStart={async (swiper: SwiperProps) => {
                const index = swiper.activeIndex;
                if (swiper.swipeDirection === 'next') {
                    const slide = filteredSlides[index - 1];
                    if (slide?.action) {
                        swiper.slidePrev();
                        await slide.action();
                        swiper.slideNext();
                    }
                }
            }}
        >
            {filteredSlides.map((slide, i) => (
                <SwiperSlide key={`slide-${i}`} style={{width: '100', height: '100%'}}>
                    <Content
                        slide={slide}
                        onDone={onDone}
                        onCancel={onCancel}
                        progress={i/(filteredSlides.length - 1)}
                    />
                </SwiperSlide>
            ))}
        </Swiper>
    );
};
